import React, { useCallback } from 'react';
import { observer } from 'mobx-react';
import { UploadFeedback } from '@ann/web-components';
import { UploadFeedbackWrapper } from './UploadFeedbackSection.styled';

/**
 * Component for displaying upload feedback with progress
 * Uses MobX observer to track changes in temporaryDocuments and uploadProgress
 */
const UploadFeedbackSection = observer(({ onClose, documentsStore, onRetry }) => {
  const handleFileRemove = useCallback((id) => {
    const doc = documentsStore.temporaryDocuments.find(d => d.uploadId === id);
    if (doc) {
      documentsStore.removeTemporaryDocument(id);
    }
  }, [documentsStore]);

  const handleRetry = useCallback((id) => {
    const doc = documentsStore.temporaryDocuments.find(d => d.uploadId === id);
    if (doc && onRetry) {
      onRetry(doc);
    }
  }, [documentsStore, onRetry]);

  // MobX observer automatically tracks changes in temporaryDocuments and uploadProgress
  const uploadFiles = documentsStore.temporaryDocuments.map(doc => {
    const progress = documentsStore.uploadProgress[doc.name];
    let status = 'ready';
    if (doc.state === 'loading') {
      status = 'uploading';
    } else if (doc.state === 'error') {
      status = 'uploadFailed';
    } else if (doc.state === 'active') {
      status = 'ready';
    }
    return {
      id: doc.uploadId,
      name: doc.name,
      progress: progress?.percentage || 0,
      status: status,
      onRetry: doc.state === 'error' ? () => handleRetry(doc.uploadId) : undefined
    };
  });

  if (uploadFiles.length === 0) return null;

  return (
    <UploadFeedbackWrapper>
      <UploadFeedback
        files={uploadFiles}
        onClose={onClose}
        onFileRemove={handleFileRemove}
        onRetry={handleRetry}
      />
    </UploadFeedbackWrapper>
  );
});

UploadFeedbackSection.displayName = 'UploadFeedbackSection';

export default UploadFeedbackSection;
